// Hold a notification-tap / deep-link target until the app can actually route it.
//
// THE BUG THIS FIXES (reported 2026-09-05, after the first announcement push):
// tapping the notification "doesn't take the user anywhere". On a COLD START
// Capacitor delivers the buffered tap the moment a listener attaches, which in
// App.tsx is a useEffect that runs on AppContent's first render — before auth
// has resolved. At that point `location.pathname` is '/', which App.tsx counts
// as a public route, so the auth-loading gate is skipped and the LOGGED-OUT
// route tree renders. Navigating to an authenticated path then matches only the
// wildcard fallback, and once the session resolves the app settles on
// /dashboard. The user sees the app open on its normal home screen and
// reasonably concludes the notification did nothing.
//
// This is not specific to the announcement: every deep-linked push (a
// scheduling-message thread, a time-off decision, a coverage request) hits the
// same cold-start race. It was never caught because push tap-routing had never
// been exercised on a real device.
//
// The fix is to make the tap wait for the session rather than race it. Kept as
// a tiny pure module so the ordering is unit-testable without mounting App.

// A second problem sits behind the first. Even once the session is ready,
// navigating is not enough: the app runs its own landing chain for a
// cold-started user ('/' -> /dashboard, and Dashboard.tsx then sends
// scheduling-only roles on to /schedules). Those redirects render AFTER the
// flush and replace the target — measured on device, a tap aimed at
// /schedules/messages ended on /schedules. So the target has to be re-asserted
// until it sticks rather than fired once and hoped for.
//
// Bounded on purpose: if MobileRouteGuard is legitimately bouncing the target
// (a path this session may not see), re-asserting forever would trap the user
// in a redirect loop. After MAX_ATTEMPTS we give up and leave them wherever the
// app put them.
const MAX_ATTEMPTS = 3;

export interface DeferredNavigator {
  /** Called with a resolved in-app path. Navigates now, or holds until ready. */
  request(path: string): void;
  /** Report whether the authenticated route tree can serve a path yet. */
  setReady(ready: boolean): void;
  /**
   * Feed every location change back in. While a target is being aimed at, a
   * path that isn't it means a redirect beat us, so we re-assert.
   */
  confirm(currentPath: string): void;
  /** Test seam: the target currently held, if any. */
  pending(): string | null;
  /** Test seam: the target currently being re-asserted, if any. */
  aiming(): string | null;
}

export function createDeferredNavigator(
  navigate: (path: string) => void,
): DeferredNavigator {
  let ready = false;
  let held: string | null = null;
  let aimedAt: string | null = null;
  let attempts = 0;

  const fire = (path: string) => {
    aimedAt = path;
    attempts = 1;
    navigate(path);
  };

  return {
    request(path: string) {
      if (!path) return;
      if (ready) {
        fire(path);
        return;
      }
      // Last one wins: if someone taps two notifications before the app is up,
      // the one they tapped most recently is the one they're waiting to see.
      held = path;
    },
    setReady(next: boolean) {
      const was = ready;
      ready = next;
      // Flush only on the false -> true edge, and only once.
      if (!was && next && held) {
        const target = held;
        held = null;
        fire(target);
      }
    },
    confirm(currentPath: string) {
      if (!aimedAt) return;
      if (currentPath === aimedAt) {
        // Landed. Stop watching so the user's own navigation is never fought.
        aimedAt = null;
        attempts = 0;
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        aimedAt = null;
        attempts = 0;
        return;
      }
      attempts += 1;
      navigate(aimedAt);
    },
    pending() {
      return held;
    },
    aiming() {
      return aimedAt;
    },
  };
}
